import { AddCircleOutlineOutlined, BarChartOutlined, Create, EditOutlined, Info, SwapHorizontalCircleOutlined } from '@mui/icons-material';
import { ToDo } from '../../ui/ToDo';
import AllowedDomainList from './allowed-domain-list';
import GeneralAdministrationPage from './general-administration';
import AppleAppCodesPage from './apple-app-codes';
import AcronymsPage from './acronyms';
import MobileNumbersPage from './site-mobile-numbers';
import { AllowedDomain, AllowedDomainListItem, emailDomainAvailable } from 'shared/services/adminApi';
import { TextField, UserField, EmailField, HyperlinkField } from '../../ui/DetailForm';
import { IApplicationModule } from '../../ui/IApplicationModule';
import { IUserSearchResult, LocationDetail } from '../../../shared/models/api/models';
import { ReactComponent as GeneralAdminIcon } from "assets/icons/settings.svg";
import { stringSort } from '../../../helpers/HelperFunctions';

// we could probably move these to the toolbar as standard text buttons
export const AddNewButtonText = "Add new";
export const ViewOrEditButtonText = "View/Edit";
export const EnableOrDisableButtonText = "Enable/Disable";

export const GeneralAdministrationModule = {
    title: "General Administration",
    className: "general-administration",
    url: "/administration/general",
    permission: "web.admin.general.view",
    icon: GeneralAdminIcon,
    component: GeneralAdministrationPage,
    children: [
        {
            title: "Allowed domain list",
            className: "allowed-domain-list",
            url: "/administration/general/allowed-domains",
            hideInMenuTile: true,
            permission: "web.admin.general.allowed-domain.read",
            selectMode: "one",
            actions: [
                {
                    name: AddNewButtonText,
                    icon: AddCircleOutlineOutlined,
                    permission: "web.admin.general.allowed-domain.create",
                },
                {
                    name: ViewOrEditButtonText,
                    icon: EditOutlined,
                    permission: "web.admin.general.allowed-domain.read",
                    minSelectedCount: 1,
                    maxSelectedCount: 1
                },
                {
                    name: EnableOrDisableButtonText,
                    icon: SwapHorizontalCircleOutlined,
                    permission: "web.admin.general.allowed-domain.enable-or-disable",
                    minSelectedCount: 1,
                    maxSelectedCount: 1
                }
            ],
            component: AllowedDomainList,
            pageSize: 20,
            views: [
                {
                    name: "view",
                    heading: "Domain details",
                    icon: Info,
                    mode: "view",
                    actions: [
                        { name: "edit", label: "Edit", switchToView: "edit" }
                    ],
                    defaultAction: "edit",
                    permission: "web.admin.general.allowed-domain.edit",
                    subSubHeading: (domain: AllowedDomain) => ("Domain details: " + domain.contractPartner),
                    subSubIcon: BarChartOutlined
                },
                {
                    name: "edit",
                    heading: "Domain details",
                    icon: Create,
                    subHeading: "Edit domain details",
                    mode: "edit",
                    actions: [
                        { name: "cancel", label: "Cancel", setResult: "close" },
                        { name: "continue", label: "Continue", switchToView: "review_edit", validate: true }
                    ],
                    defaultAction: "continue",
                    permission: "web.admin.general.allowed-domain.edit",
                    subSubHeading: (domain: AllowedDomain) => ("Domain details: " + domain.contractPartner),
                    subSubIcon: BarChartOutlined
                },
                {
                    name: "review_edit",
                    heading: "Domain details",
                    icon: Create,
                    subHeading: "Check and confirm domain details",
                    mode: "review",
                    actions: [
                        { name: "edit", label: "Edit", switchToView: "edit" },
                        { name: "save", label: "Update details", setResult: "save" }
                    ],
                    defaultAction: "save",
                    permission: "web.admin.general.allowed-domain.edit",
                    subSubHeading: (domain: AllowedDomain) => ("Domain details: " + domain.contractPartner),
                    subSubIcon: BarChartOutlined
                },
                {
                    name: "new",
                    heading: "Add new domain",
                    icon: AddCircleOutlineOutlined,
                    subHeading: "Enter domain details",
                    mode: "new",
                    progress: "1 / 2",
                    actions: [
                        { name: "cancel", label: "Cancel", setResult: "close" },
                        { name: "continue", label: "Continue", switchToView: "review_new", validate: true }
                    ],
                    defaultAction: "continue",
                    permission: "web.admin.general.allowed-domain.edit",
                    subSubHeading: (domain: AllowedDomain) => ("Domain details")
                },
                {
                    name: "review_new",
                    heading: "Add new domain",
                    icon: AddCircleOutlineOutlined,
                    subHeading: "Check and confirm domain details",
                    mode: "review",
                    progress: "2 / 2",
                    actions: [
                        { name: "edit", label: "Edit", switchToView: "new" },
                        { name: "save", label: "Upload", setResult: "save", msg: "The domain has been added to the list." }
                    ],
                    defaultAction: "save",
                    permission: "web.admin.general.allowed-domain.edit"
                }
            ],
            columns: [
                {
                    name: "contractPartner",
                    heading: "Domain name",
                    className: "string-col",
                    showInGrid: 1,
                    sort: (a: AllowedDomainListItem, b: AllowedDomainListItem, order: number) =>
                        (stringSort(a.contractPartner, b.contractPartner, true) * order)
                },
                {
                    name: "contractPartner",
                    heading: "Contract partner",
                    className: "string-col",
                    formComponent: TextField,
                    validation: {
                        required: true,
                        minLength: 2,
                        tip: "Contract partner is mandatory"
                    },
                    placeholderText: "Enter company name",
                    setValue: (allowedDomain: AllowedDomain, val: string) => { allowedDomain.contractPartner = val; }
                },
                {
                    name: "isEnabled",
                    heading: "Domain status",
                    format: (val: any) => (val === true ? "Enabled" : "Disabled"),
                    filterable: true,
                    className: "boolean-col",
                    showInGrid: 2
                },
                {
                    name: "sector",
                    heading: "Sector",
                    className: "string-col",
                    formComponent: TextField,
                    validation: {
                        required: true,
                        minLength: 1,
                        tip: "Sector is mandatory"
                    },
                    placeholderText: "Enter specialised area",
                    setValue: (allowedDomain: AllowedDomain, val: string) => { allowedDomain.sector = val; }
                },
                {
                    name: "companyWebsite",
                    heading: "Company website",
                    className: "string-col",
                    formComponent: HyperlinkField,
                    validation: {
                        required: true,
                        startsWith: ["http://", "https://"],
                        segments: { splitter: '.', min: 2 },
                        minLength: 10,
                        maxLength: 100,
                        tip: "Company website is mandatory and must be a valid url e.g. https://www.bhp.com"
                    },
                    placeholderText: "Enter url e.g. https://www.bhp.com",
                    tip: "Enter the company website e.g. https://www.bhp.com",
                    setValue: (allowedDomain: AllowedDomain, val: string) => { allowedDomain.companyWebsite = val; }
                },
                {
                    name: "emailDomain",
                    heading: "Company email domain",
                    className: "string-col",
                    readonlyForEdit: true,
                    formComponent: TextField,
                    validation: {
                        required: true,
                        startsWith: ["@"],
                        segments: { splitter: '.', min: 2 },
                        minLength: 5,
                        immediateValidationFn: (async (emailDomain: string) => { return emailDomainAvailable(emailDomain) }),
                        immediateValidationMsgFn: ((emailDomain) => `Email domain ${ emailDomain } is already in use`),

                        tip: "Company email domain is mandatory and must be a valid domain e.g. @bhp.com"
                    },
                    placeholderText: "Enter email domain e.g. @bhp.com",
                    tip: "Enter the email domain e.g. @bhp.com",
                    setValue: (allowedDomain: AllowedDomain, val: string) => { allowedDomain.emailDomain = val; }
                },
                {
                    name: "contractManager",
                    heading: "BHP contract manager/supervisor",
                    className: "user-id",
                    formComponent: UserField,
                    validation: {
                        required: true
                    },
                    placeholderText: "Enter first name or last name",
                    tip: "List only contains users registered in the system",
                    setValue: (allowedDomain: AllowedDomain, val: IUserSearchResult) => {
                        allowedDomain.contractManager = val;
                    }
                },
                {
                    name: "contractManager",
                    heading: "BHP contract manager/supervisor email",
                    className: "user-email",
                    formComponent: EmailField,
                    readonly: true,
                    format: (val: IUserSearchResult) => val.email,
                    formatRow: (row: AllowedDomain) => row.contractManager ? row.contractManager.email : ""
                }
            ]
        },
        {
            title: "ABM Redeem Codes",
            permission: "web.admin.role.update",
            hideInMenuTile: true,
            actions: [],
            url: "/administration/general/abm-redeem-codes",
            component: AppleAppCodesPage
        },
        {
            title: "Acronyms",
            className: "acronyms",
            permission: "web.admin.general.acronyms.read",
            hideInMenuTile: true,
            actions: [],
            url: "/administration/general/acronyms",
            component: AcronymsPage
        },
        {
            title: "Site mobile numbers",
            permission: "web.admin.general.view",
            hideInMenuTile: true,
            selectMode: "one",
            actions: [

                {
                    name: ViewOrEditButtonText,
                    icon: EditOutlined,
                    permission: "web.admin.fatigue-assessment.settings",
                    minSelectedCount: 1,
                    maxSelectedCount: 1
                }

            ],
            columns: [{
                name: "name",
                heading: "Site",
                className: "string-col",
                showInGrid: 1,
                sort: (a: LocationDetail, b: LocationDetail, order: number) =>
                    (stringSort(a.name, b.name, true) * order)
            },
            {
                name: "siteMobileNumber",
                heading: "Mobile Number",
                className: "string-col",
                showInGrid: 2,
                sort: (a: LocationDetail, b: LocationDetail, order: number) =>
                    (stringSort(a.siteMobileNumber, b.siteMobileNumber, true) * order)
            },
            {
                name: "siteMobileNumber",
                heading: "Mobile Number",
                className: "string-col",
                formComponent: TextField,
                setValue: (location: LocationDetail, val: string) => { location.siteMobileNumber = val; },
                placeholderText: "Enter site mobile",
                validation: {
                    required: true,
                    minLength: 10,
                    maxLength: 15,
                    immediateValidationFn: (mobileString) => {
                        var regex = /^(\+\d{1,3}[- ]?)?\d{10}$/g
                        return Promise.resolve(regex.test(mobileString));
                    },
                    immediateValidationMsgFn: ((mobile) => `Not a valid mobile number`),
                }
            }
            ],
            views: [
                {
                    name: "edit",
                    heading: "Site Mobile Number",
                    icon: Create,
                    subHeading: "Edit site mobile number",
                    mode: "quickEdit",
                    actions: [
                        { name: "cancel", label: "Cancel", setResult: "close" },
                        { name: "save", label: "Save", setResult: "save", validate: true }
                    ],
                    defaultAction: "save",
                    permission: "web.admin.fatigue-assessment.settings",
                    subSubHeading: (location: LocationDetail) => ("Site: " + location.name),
                    subSubIcon: BarChartOutlined
                }
            ],
            url: "/administration/general/site-mobile-numbers",
            component: MobileNumbersPage
        }
    ]
} as IApplicationModule;